import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  HostListener,
  inject,
  OnInit
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Router } from '@angular/router';
import { GridOverrides, RowSelection } from '@mds/common';
import { MdsIcon, MdsIconsModule } from '@mds/icons';
import {
  ButtonToggleComponent,
  ChipComponent,
  DropdownMenuComponent,
  GridModule,
  GridNoRowsComponent,
  GridTagComponent,
  LoaderModule,
  RadioGroupComponent
} from '@mds/ui';
import { GetContextMenuItemsParams, GridApi, IGetRowsParams, MenuItemDef } from 'ag-grid-community';
import * as Highcharts from 'highcharts';
import { HighchartsChartModule } from 'highcharts-angular';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '@dialogs/confirmation-dialog/confirmation-dialog.component';
import { DashboardAppsComponent } from '@pages/dashboards/shared/dashboard-apps/dashboard-apps.component';
import { LocalStorageService } from '@services/local-storage.service';
import { MenuService } from '@services/menu.service';
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, map, switchMap, tap } from 'rxjs/operators';
import { GreetingComponent } from '../shared/greeting/greeting.component';
import { HotspotChartsComponent } from './components/hotspot-charts/hotspot-charts.component';
import { FiltersService } from './services/filters.service';
import { Scope3Service } from './services/scope-3.service';
import { DATA_QUALITY_OPTIONS } from './utils/chart-options';
import {
  Aggregation,
  AggregationOptions,
  ButtonTypes,
  DataQualityColors,
  dataQualityEnum,
  DataQualityOptions,
  PeriodOptions,
  SiteRole
} from './utils/constants';

@Component({
  selector: 'app-scope-3',
  standalone: true,
  imports: [
    CommonModule,
    GridModule,
    MatIconModule,
    HighchartsChartModule,
    MdsIconsModule,
    ChipComponent,
    RadioGroupComponent,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    LoaderModule,
    DropdownMenuComponent,
    ReactiveFormsModule,
    GreetingComponent,
    HotspotChartsComponent,
    ButtonToggleComponent,
    // AllPipesModule,
    MatDialogModule,
    DashboardAppsComponent
  ],
  templateUrl: './scope-3.component.html',
  styleUrls: ['./scope-3.component.scss'],
  providers: [Scope3Service, FiltersService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class Scope3Component implements OnInit {
  icons = MdsIcon;
  dataQualityColors = DataQualityColors;
  aggregationOptions = AggregationOptions;
  dataQualityDropDownOptions = DataQualityOptions;
  periodDropDownOptions = PeriodOptions;
  yearDropDownOptions = this.getYears().map((year) => ({
    text: year.toString(),
    value: year
  }));
  loading = true;
  Highcharts = Highcharts;
  dataQualityOptions: Highcharts.Options;

  overrides: GridOverrides;
  gridApi: GridApi;

  private scope3Service = inject(Scope3Service);
  private filtersService = inject(FiltersService);
  private localStorageService = inject(LocalStorageService);
  private cdr = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);
  private menuService = inject(MenuService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  lastUpdateTime: string;
  isSwitchView = true;
  filtersForm = new FormGroup({
    aggregation: new FormControl(Aggregation.SUPPLIER),
    category: new FormControl([]),
    dataQuality: new FormControl([]),
    spendClass: new FormControl([]),
    buyerSite: new FormControl([]),
    supplierSite: new FormControl([]),
    productGroup: new FormControl([]),
    year: new FormControl([this.yearDropDownOptions[0]]),
    period: new FormControl([this.periodDropDownOptions[0]])
  });
  aggreagationValue = AggregationOptions.find((data) => data.value === this.filtersForm.get('aggregation').value);
  toggleData = [
    {
      value: 'Table',
      icon: MdsIcon.TABLE,
      label: 'Table',
      ariaLabel: 'Table Icon'
    },
    {
      value: 'Graph',
      icon: MdsIcon.HOTSPOTS,
      label: 'Graph',
      ariaLabel: 'Graph Icon'
    }
  ];
  // Added to get saved aggregation data
  categorySearchValue$ = new BehaviorSubject('');
  spendClassSearchValue$ = new BehaviorSubject('');
  buyerSiteSearchValue$ = new BehaviorSubject('');
  supplierSiteSearchValue$ = new BehaviorSubject('');
  productGroupSearchValue$ = new BehaviorSubject('');
  // Added to get saved aggregation data
  savedAggregations$ = new BehaviorSubject<any>(this.getSavedAggregations());
  // Fetch Spend class options based on search value if required otherwise show previous selected values
  filteredSpendClassOptions$: Observable<any[]>;
  // Fetch Buyer Sites based on search value if required otherwise show previous selected values
  filteredBuyerSiteOptions$: Observable<any[]>;
  // Fetch Buyer Sites based on search value if required otherwise show previous selected values
  filteredSupplierSiteOptions$: Observable<any[]>;
  // Fetch Product group based on search value if required otherwise show previous selected values
  filteredProductGroupOptions$: Observable<any[]>;
  pageLimit = 20;
  clientHeight = window.innerHeight;
  currentTableData: any;
  categoryOptions$ = this.scope3Service.getCategories();
  filteredCategoryOptions$ = combineLatest([this.categoryOptions$, this.categorySearchValue$]).pipe(
    map(([options, searchValue]) =>
      options.filter((option) => option.name.toLowerCase().includes(searchValue.toLowerCase()))
    ),
    map((options) => options.map((option) => this.filtersService.formatDropdownOption(option)))
  );
  get commonFilters() {
    const filters = this.filtersForm.getRawValue();
    const commonFilters: any = {
      aggregate_by: filters.aggregation,
      category_scope3: filters.category.map((c) => c.value) || [],
      product_group_rids: filters.productGroup.map((c) => c.value) || [],
      quality_name: filters.dataQuality.map((c) => c.value) || [],
      receiver_site_rids: filters.buyerSite.map((c) => c.value) || [],
      supplier_site_rids: filters.supplierSite.map((c) => c.value) || [],
      spend_class: filters.spendClass.map((c) => c.value) || [],
      year: filters.year[0].value
    };
    if (filters.period[0].value) {
      commonFilters.quarter_range = filters.period[0].value;
    }
    return commonFilters;
  }

  get tableFilters() {
    return {
      common_filters: this.commonFilters,
      limit: this.pageLimit,
      page: 1,
      table_filters: null,
      table_sorting: null
    };
  }

  refreshSpendClassOptions() {
    this.filteredSpendClassOptions$ = this.filtersService.createFilteredOptions(
      this.spendClassSearchValue$,
      (search) => this.scope3Service.getSpendClasses(search) as Observable<any[]>,
      this.filtersService.formatDropdownOption,
      this.savedAggregations$.getValue() ? this.savedAggregations$.getValue().spendClass : []
    );
  }

  onSpendClassDropdownOpened() {
    this.refreshSpendClassOptions();
  }

  refreshBuyerSiteOptions() {
    this.filteredBuyerSiteOptions$ = this.filtersService.createFilteredOptions(
      this.buyerSiteSearchValue$,
      (search) => this.scope3Service.getSites(search, SiteRole.RECEIVER),
      this.filtersService.formatDropdownOption,
      this.savedAggregations$.getValue() ? this.savedAggregations$.getValue().buyerSite : []
    );
  }

  onBuyerSiteDropdownOpened() {
    this.refreshBuyerSiteOptions();
  }

  refreshSupplierSiteOptions() {
    this.filteredSupplierSiteOptions$ = this.filtersService.createFilteredOptions(
      this.supplierSiteSearchValue$,
      (search) => this.scope3Service.getSites(search, SiteRole.SUPPLIER),
      this.filtersService.formatDropdownOption,
      this.savedAggregations$.getValue() ? this.savedAggregations$.getValue().supplierSite : []
    );
  }

  onSupplierSiteDropdownOpened() {
    this.refreshSupplierSiteOptions();
  }

  refreshProductGroupOptions() {
    this.filteredProductGroupOptions$ = this.filtersService.createFilteredOptions(
      this.productGroupSearchValue$,
      (search) => this.scope3Service.getProductGroup(search),
      this.filtersService.formatDropdownOption,
      this.savedAggregations$.getValue() ? this.savedAggregations$.getValue().productGroup : []
    );
  }

  onProductGroupDropdownOpened() {
    this.refreshProductGroupOptions();
  }

  ngOnInit() {
    this.menuService.clearMenuConfig();

    this.init();
    this.initFilterValueChange();
    this.menuService.reload$
      .pipe(
        filter((reload) => !!reload),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.init();
      });
  }

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    if (this.gridApi) {
      setTimeout(() => this.adjustGridColumns(), 100);
    }
  }

  onGridReady(gridApi: GridApi) {
    this.gridApi = gridApi;

    this.adjustGridColumns();

    this.gridApi.setGridOption('datasource', this.createDatasource());
  }

  adjustGridColumns() {
    if (this.gridApi) {
      this.gridApi.sizeColumnsToFit();
    }
  }

  initFilterValueChange() {
    this.filtersForm.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.fetchDataQualityAndResetGridDatasource();
      });
  }

  fetchDataQualityAndResetGridDatasource() {
    this.loading = true;
    const formValue = this.filtersForm.value;
    this.localStorageService.setItem('scope3.aggregations', formValue);
    this.savedAggregations$.next(formValue);
    this.gridApi?.setGridOption('datasource', this.createDatasource());
    this.cdr.markForCheck();

    this.scope3Service
      .getDataQuality(this.commonFilters)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.menuService.setReloadState(false);
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (dataQuality) => {
          this.updateDataQualityChart(dataQuality);
        },
        error: () => {
          this.getGridOverrides();
          this.updateDataQualityChart([]);
        }
      });
  }

  onNoDataClick() {
    this.router.navigate(['/importer/transaction_import']);
  }

  onRowClicked(event: any) {
    const columnField = event.api.getFocusedCell()?.column.getColId();
    if (columnField === 'sku') {
      const sku_rid = this.getRid(event.data.sku);

      const url = `/mattermaps/Climate change - EF 3.1 Method (adapted)/${sku_rid.replace('#', '')}`;
      this.router.navigate([url], {});
    } else if (columnField === 'supplier_site') {
      const supplier_rid = this.getRid(event.data.supplier_site);
      this.router.navigate(['/supplier-breakdown', supplier_rid.replace('#', '')]);
    }
  }

  onSwitchView() {
    this.init();
    this.isSwitchView = !this.isSwitchView;
  }

  aggregationMenu(event: any) {
    this.filtersForm.patchValue({
      aggregation: event[0].value
    });
    this.aggreagationValue = { ...event[0] };
  }

  getContextMenuItems = (params: GetContextMenuItemsParams<any>): (string | MenuItemDef)[] => {
    const defaultItems = params.defaultItems || [];

    return defaultItems.map((item) => {
      // Replace 'export' with custom server export options
      if (item === 'export') {
        return {
          name: 'Export',
          subMenu: [
            {
              name: 'CSV Export',
              action: () => this.exportFromServer('csv')
            },
            {
              name: 'Excel Export',
              action: () => this.exportFromServer('xlsx')
            }
          ]
        };
      }
      return item;
    });
  };

  exportFromServer(mode: string) {
    const payload = {
      dashboard_request_serializer: this.tableFilters,
      file_name: `export.${mode}`,
      file_type: mode
    };
    this.scope3Service.exportData(payload).subscribe((res) => {
      if (res.status !== 'failed') {
        this.showExportOperationStatus('The export data operation started and it will be available in Jobs page.');
      } else {
        this.showExportOperationStatus(res.error);
      }
    });
  }

  showExportOperationStatus(message: string) {
    this.dialog.open(ConfirmationDialogComponent, {
      disableClose: true,
      data: {
        type: ButtonTypes.INFO,
        header: 'Export Data Status',
        text: message,
        confirm_text: 'Ok'
      }
    });
  }

  private init() {
    this.loading = true;
    this.getGridOverrides();
    const savedAggregations = this.localStorageService.getItem('scope3.aggregations') ?? this.filtersForm.value;
    this.filtersForm.patchValue(savedAggregations, { emitEvent: true });
    this.fetchDataQualityAndResetGridDatasource();
  }

  private updateDataQualityChart(dataQuality: any) {
    let dataQualitySeries = Object.entries(dataQuality).map(([key, value]: [string, number]) => ({
      type: 'bar',
      name: `${value.toFixed(2)}% ${dataQualityEnum[key]}`,
      data: [value],
      color: this.dataQualityColors[key],
      enableMouseTracking: false
    }));
    if (!dataQualitySeries.length) {
      dataQualitySeries = [
        {
          type: 'bar',
          name: 'Unknown',
          data: [100],
          color: '#DEE5E8',
          enableMouseTracking: false
        }
      ];
    }
    this.dataQualityOptions = this.getDataQualityOptions(dataQualitySeries);
  }

  private getDataQualityOptions(series: any[]) {
    return {
      ...structuredClone(DATA_QUALITY_OPTIONS),
      series: [...series],
      legend: {
        padding: 0, // Remove extra padding
        itemMarginLeft: 0, // Remove left margin for items
        itemMarginRight: 0,
        width: '100%',
        navigation: { enabled: false }
      },

      chart: {
        backgroundColor: 'rgba(255,255,255,0)',
        height: 100
      }
    } as Highcharts.Options;
  }

  private getGridOverrides() {
    this.overrides = {
      columnDefs: [],
      defaultColDef: {
        lockPosition: false,
        lockPinned: false,
        sortable: true,
        resizable: true,
        filter: false,
        floatingFilter: false
      },
      groupDefaultExpanded: -1,
      rowSelection: RowSelection.SINGLE,
      treeGrid: false,
      paginationAutoPageSize: false,
      pagination: true,
      paginationPageSize: 20,
      rowModelType: 'infinite',
      noRowsOverlayComponent: GridNoRowsComponent,
      noRowsOverlayComponentParams: {
        title: 'No transaction data to show the table',
        description: 'Import data and it will start showing rows here',
        onClick: this.onNoDataClick,
        buttonLabel: 'Add transaction files'
      },
      getContextMenuItems: this.getContextMenuItems
    };
    setTimeout(() => this.gridApi?.sizeColumnsToFit(), 0);
    this.cdr.markForCheck();
  }

  private getColumnDefs(columns: any[]) {
    return columns.map((column) => {
      if (column.key === 'impact_source') {
        return {
          field: column.key,
          headerName: column.label,
          filter: 'agTextColumnFilter',
          minWidth: 150,
          cellRenderer: GridTagComponent,
          cellStyle: (params) => {
            return params.colDef.field === 'sku' || params.colDef.field === 'supplier_site'
              ? {
                  color: '#4367DE',
                  textDecoration: 'underline',
                  cursor: 'pointer'
                }
              : {};
          }
        };
      }
      return {
        field: column.key,
        headerName: column.label,
        filter: 'agTextColumnFilter',
        minWidth: 150,
        tooltipField: column.key,
        hide: !!column.hidden,
        cellStyle: () => {
          if (['sku', 'supplier_site'].includes(column.key)) {
            return {
              cursor: 'pointer',
              textDecoration: 'underline',
              color: '#4367DE'
            };
          }
          return {};
        }
      };
    });
  }

  private getRowData(rows: Array<{ key: string; display_value: string }[]>) {
    const rowData = [];
    if (!rows) return rowData;

    rows.forEach((row) => {
      const rowObj = {};
      row.forEach((r: { key: string; display_value: string }) => {
        rowObj[r.key] = r.display_value;
      });
      rowData.push(rowObj);
    });
    return rowData;
  }

  private createDatasource() {
    return {
      getRows: (params: IGetRowsParams) => {
        const page = Math.floor(params.startRow / this.pageLimit) + 1;
        const tableFilters = {
          ...this.tableFilters,
          page,
          table_sorting: params.sortModel
            ? params.sortModel.map(({ colId, sort, ...rest }) => ({
                column_id: colId,
                order: sort,
                ...rest
              }))
            : []
        };

        this.gridApi.setGridOption('loading', true);
        this.scope3Service
          .getTableData(tableFilters)
          .pipe(
            tap((response: any) => {
              this.lastUpdateTime = response?.last_updated ?? null;
              this.currentTableData = response.lines;

              if (!response?.lines || !response.lines.length) {
                this.gridApi.setGridOption('loading', false);
                this.gridApi.showNoRowsOverlay();
                params.successCallback([], 0);
              } else {
                // Render table data immediately
                const columnDefs = this.getColumnDefs(response.lines[0]);
                this.gridApi.setGridOption('columnDefs', columnDefs);
                const tableData = this.getRowData(response.lines);
                params.successCallback(tableData, undefined);
                this.gridApi.sizeColumnsToFit();
                this.gridApi.hideOverlay();
                this.gridApi.setGridOption('loading', false);
              }
              this.cdr.markForCheck();
            }),
            switchMap((response: any) =>
              this.scope3Service.getTableCount(this.commonFilters).pipe(
                map((totalCount: number) => ({
                  lines: response.lines,
                  totalCount
                }))
              )
            ),
            catchError(() => {
              params.failCallback();
              this.getGridOverrides();
              this.gridApi.showNoRowsOverlay();
              return of({ lines: [], totalCount: 0 });
            }),
            takeUntilDestroyed(this.destroyRef) // Ensure cleanup
          )
          .subscribe(({ lines, totalCount }: { lines: any[]; totalCount: number }) => {
            const tableData = this.getRowData(lines);
            params.successCallback(tableData, totalCount);
          });
      }
    };
  }

  private getSavedAggregations() {
    return this.localStorageService?.getItem('scope3.aggregations') ?? {};
  }

  private getRid(columnValue: any): string {
    return this.findValueByDisplayValue(this.currentTableData, columnValue);
  }

  private findValueByDisplayValue = (data: any[][], targetDisplayValue: any): any =>
    data.flat().find((item: any) => typeof item === 'object' && item.display_value === targetDisplayValue)?.value ||
    null;

  private getYears(): number[] {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, index) => currentYear - index);
  }
}
